import type {
  CreateGroupChatSessionRequest,
  DataResponse,
  GroupChatAttachmentResponse,
  GroupChatMessage,
  GroupChatSession,
  InterruptGroupChatRequest,
  SendGroupChatMessageRequest,
  SessionSharingResponse,
  SharedGroupChatSessionSummary,
  ShareLinkResponse,
  SharePermission,
  ShareResponse,
  SpecialistAgent,
} from "@/types";
import { api } from "./api";

const BASE_PATH = "/group-chat";

/**
 * Group Chat API Service
 */
export const groupChatService = {
  /**
   * List all specialist agents
   */
  async listSpecialistAgents(): Promise<SpecialistAgent[]> {
    const response = await api.get<DataResponse<SpecialistAgent[]>>(`${BASE_PATH}/specialist-agents`);
    return response.data;
  },

  /**
   * List all group chat sessions
   */
  async listSessions(
    page: number = 1,
    pageSize: number = 50,
    starredOnly: boolean = false,
  ): Promise<{
    data: GroupChatSession[];
    meta: { total: number; page: number; pageSize: number; totalPages: number };
  }> {
    const params = new URLSearchParams({
      page: page.toString(),
      page_size: pageSize.toString(),
    });
    if (starredOnly) params.set("starred_only", "true");
    const response = await api.get<{
      data: GroupChatSession[];
      meta: { total: number; page: number; pageSize: number; totalPages: number };
    }>(`${BASE_PATH}/sessions?${params}`);
    return response;
  },

  /**
   * Create a new group chat session
   */
  async createSession(request: CreateGroupChatSessionRequest): Promise<GroupChatSession> {
    const response = await api.post<DataResponse<GroupChatSession>>(`${BASE_PATH}/sessions`, request);
    return response.data;
  },

  /**
   * Get a specific group chat session by UUID
   */
  async getSession(sessionUuid: string): Promise<GroupChatSession> {
    const response = await api.get<DataResponse<GroupChatSession>>(`${BASE_PATH}/sessions/${sessionUuid}`);
    return response.data;
  },

  /**
   * Update a group chat session (title, starred status, etc.)
   */
  async updateSession(
    sessionUuid: string,
    updates: { title?: string; isStarred?: boolean; orchestratorVisible?: boolean; reasoningEffort?: string | null },
  ): Promise<GroupChatSession> {
    const response = await api.patch<DataResponse<GroupChatSession>>(`${BASE_PATH}/sessions/${sessionUuid}`, updates);
    return response.data;
  },

  /**
   * Delete a group chat session
   */
  async deleteSession(sessionUuid: string): Promise<void> {
    await api.delete(`${BASE_PATH}/sessions/${sessionUuid}`);
  },

  /**
   * Send a message to the group chat (triggers multi-agent discussion)
   * Returns array of messages from orchestrator and agents
   */
  async sendMessage(sessionUuid: string, request: SendGroupChatMessageRequest): Promise<GroupChatMessage[]> {
    const response = await api.post<DataResponse<GroupChatMessage[]>>(
      `${BASE_PATH}/sessions/${sessionUuid}/messages`,
      request,
    );
    return response.data;
  },

  /**
   * Interrupt an active group chat discussion
   */
  async interruptDiscussion(sessionUuid: string, request: InterruptGroupChatRequest = {}): Promise<GroupChatSession> {
    const response = await api.post<DataResponse<GroupChatSession>>(
      `${BASE_PATH}/sessions/${sessionUuid}/interrupt`,
      request,
    );
    return response.data;
  },

  /**
   * Upload image attachments for a group chat session.
   * Returns attachment metadata including UUIDs to reference when sending a message.
   */
  async uploadAttachments(sessionUuid: string, files: File[]): Promise<DataResponse<GroupChatAttachmentResponse[]>> {
    return api.upload<DataResponse<GroupChatAttachmentResponse[]>>(
      `${BASE_PATH}/sessions/${sessionUuid}/attachments`,
      files,
    );
  },

  /**
   * Send a message and start group chat streaming via OperationManager.
   * Returns an operation_id to subscribe to via useOperationStream.
   */
  async sendStreamMessage(
    sessionUuid: string,
    request: SendGroupChatMessageRequest,
  ): Promise<{ operation_id: string }> {
    return api.post<{ operation_id: string }>(`${BASE_PATH}/sessions/${sessionUuid}/messages/stream`, request);
  },

  /**
   * Trigger on-demand synthesis of the latest agent discussion.
   * Returns the generated synthesis message.
   */
  async synthesize(sessionUuid: string): Promise<GroupChatMessage> {
    const response = await api.post<DataResponse<GroupChatMessage>>(
      `${BASE_PATH}/sessions/${sessionUuid}/synthesize`,
      {},
    );
    return response.data;
  },

  // Sharing
  getShares: (sessionId: string) =>
    api.get<DataResponse<SessionSharingResponse>>(`${BASE_PATH}/sessions/${sessionId}/sharing`),
  shareWithUser: (sessionId: string, userUuid: string, permission: SharePermission) =>
    api.post<DataResponse<ShareResponse>>(`${BASE_PATH}/sessions/${sessionId}/sharing/users`, {
      user_uuid: userUuid,
      permission,
    }),
  removeShare: (sessionId: string, userUuid: string) =>
    api.delete(`${BASE_PATH}/sessions/${sessionId}/sharing/users/${userUuid}`),
  createShareLink: (sessionId: string, permission: SharePermission) =>
    api.post<DataResponse<ShareLinkResponse>>(`${BASE_PATH}/sessions/${sessionId}/sharing/link`, {
      permission,
    }),
  removeShareLink: (sessionId: string, linkUuid: string) =>
    api.delete(`${BASE_PATH}/sessions/${sessionId}/sharing/link/${linkUuid}`),
  getSharedWithMe: () => api.get<DataResponse<SharedGroupChatSessionSummary[]>>(`${BASE_PATH}/shared-with-me`),
  accessShareLink: (token: string) => api.get<DataResponse<GroupChatSession>>(`${BASE_PATH}/shared/${token}`),
};
